import { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import PersonAddRoundedIcon from '@mui/icons-material/PersonAddRounded'
import EventRoundedIcon from '@mui/icons-material/EventRounded'
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded'
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded'
import {
  addToCalendar,
  CAL_PROVIDERS,
  getPreferredProvider,
  PROVIDER_LABELS,
  setPreferredProvider,
  type CalEvent,
  type CalProvider,
} from '../utils/calendar'
import {
  networkingService,
  type ConnectionRow,
  type DirectoryEntry,
  type MeetingRow,
  type MyProfile,
} from '../services/networkingService'
import PageContainer from '../components/ui/PageContainer'
import ProfileAvatar from '../components/ProfileAvatar'
import { EmptyState, LoadingState } from '../components/ui/States'
import { formatDayLabel, formatTimeRange } from '../utils/format'
import { useAttendeeAuth } from '../contexts/AttendeeAuthContext'

export default function NetworkPage() {
  const { isAuthenticated } = useAttendeeAuth()
  const [profile, setProfile] = useState<MyProfile | null>(null)
  const [tab, setTab] = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false)
      return
    }
    networkingService
      .getProfile()
      .then(setProfile)
      .catch(() => setProfile(null))
      .finally(() => setLoading(false))
  }, [isAuthenticated])

  if (loading) return <LoadingState />
  if (!isAuthenticated)
    return (
      <PageContainer>
        <EmptyState title="Sign in to network" description="Connect with other attendees once you're signed in." />
      </PageContainer>
    )
  // Profile load failed (e.g. an expired session) — degrade gracefully instead
  // of crashing on a null profile (which surfaced as "Something went wrong").
  if (!profile)
    return (
      <PageContainer title="Network" subtitle="Meet other attendees">
        <EmptyState
          title="Networking unavailable"
          description="We couldn't load your networking profile. Please refresh, or sign in again if the problem continues."
        />
      </PageContainer>
    )

  const toggleOptIn = async (v: boolean) => {
    const updated = await networkingService.updateProfile({ optIn: v })
    setProfile(updated)
  }

  return (
    <PageContainer title="Network" subtitle="Meet other attendees">
      {!profile.optIn ? (
        <Card variant="outlined" sx={{ p: 3, borderRadius: 3, textAlign: 'center' }}>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Join the attendee directory
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Opt in to appear in the directory and let others connect and request
            meetings with you. You can opt out anytime.
          </Typography>
          <Button variant="contained" onClick={() => toggleOptIn(true)}>
            Opt in to networking
          </Button>
        </Card>
      ) : (
        <>
          <ProfileCard profile={profile} onChange={setProfile} />
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ my: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Tab label="Directory" sx={{ fontWeight: 700, textTransform: 'none' }} />
            <Tab label="Connections" sx={{ fontWeight: 700, textTransform: 'none' }} />
            <Tab label="Meetings" sx={{ fontWeight: 700, textTransform: 'none' }} />
          </Tabs>
          {tab === 0 && <DirectoryTab />}
          {tab === 1 && <ConnectionsTab />}
          {tab === 2 && <MeetingsTab />}
        </>
      )}
    </PageContainer>
  )
}

function ProfileCard({ profile, onChange }: { profile: MyProfile; onChange: (p: MyProfile) => void }) {
  const [headline, setHeadline] = useState(profile.headline ?? '')
  const [bio, setBio] = useState(profile.bio ?? '')
  const [linkedin, setLinkedin] = useState(profile.linkedinUrl ?? '')
  const save = async () => {
    const updated = await networkingService.updateProfile({
      headline: headline || null,
      bio: bio || null,
      linkedinUrl: linkedin || null,
    })
    onChange(updated)
  }
  return (
    <Card variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
        <ProfileAvatar avatar={profile} name={profile.headline} size={48} />
        <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>
          Your networking profile
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={profile.optIn}
              onChange={async (e) => onChange(await networkingService.updateProfile({ optIn: e.target.checked }))}
            />
          }
          label="Visible"
        />
      </Stack>
      <Stack spacing={1.5}>
        <TextField size="small" label="Headline (e.g. VP Marketing at Acme)" fullWidth value={headline} onChange={(e) => setHeadline(e.target.value)} onBlur={save} />
        <TextField size="small" label="Bio" fullWidth multiline minRows={2} value={bio} onChange={(e) => setBio(e.target.value)} onBlur={save} />
        <TextField size="small" label="LinkedIn URL" fullWidth value={linkedin} onChange={(e) => setLinkedin(e.target.value)} onBlur={save} />
        <Typography variant="caption" color="text.secondary">
          To change your photo or avatar, use “Complete your profile” — updates
          here are saved for your future events too.
        </Typography>
      </Stack>
    </Card>
  )
}

function DirectoryTab() {
  const [rows, setRows] = useState<DirectoryEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [meetingWith, setMeetingWith] = useState<DirectoryEntry | null>(null)

  const load = () =>
    networkingService.directory().then(setRows).catch(() => setRows([])).finally(() => setLoading(false))
  useEffect(() => {
    load()
  }, [])

  const connect = async (e: DirectoryEntry) => {
    await networkingService.requestConnection(e.attendeeId)
    load()
  }

  if (loading) return <LoadingState />
  if (rows.length === 0) return <EmptyState title="No one here yet" description="Attendees who opt in will appear here." />

  return (
    <>
      <Stack spacing={1.5}>
        {rows.map((e) => {
          const c = e.connection
          return (
            <Card key={e.attendeeId} variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <ProfileAvatar avatar={e} name={e.fullName} size={44} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {e.fullName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {e.headline || [e.company, e.track].filter(Boolean).join(' · ')}
                  </Typography>
                  {e.email && (
                    <Typography variant="caption" color="primary.main">
                      {e.email}
                    </Typography>
                  )}
                </Box>
                <Stack spacing={0.5} alignItems="flex-end">
                  {!c && (
                    <Button size="small" variant="outlined" startIcon={<PersonAddRoundedIcon />} onClick={() => connect(e)}>
                      Connect
                    </Button>
                  )}
                  {c?.status === 'pending' && (
                    <Chip size="small" label={c.direction === 'outgoing' ? 'Requested' : 'Wants to connect'} />
                  )}
                  {c?.status === 'accepted' && <Chip size="small" color="success" label="Connected" />}
                  <Button size="small" startIcon={<EventRoundedIcon />} onClick={() => setMeetingWith(e)}>
                    Meet
                  </Button>
                </Stack>
              </Stack>
            </Card>
          )
        })}
      </Stack>
      {meetingWith && (
        <MeetingDialog entry={meetingWith} onClose={() => setMeetingWith(null)} />
      )}
    </>
  )
}

function ConnectionsTab() {
  const [rows, setRows] = useState<ConnectionRow[]>([])
  const [loading, setLoading] = useState(true)
  const load = () => networkingService.connections().then(setRows).catch(() => setRows([])).finally(() => setLoading(false))
  useEffect(() => {
    load()
  }, [])
  const respond = async (id: string, accept: boolean) => {
    await networkingService.respondConnection(id, accept)
    load()
  }
  if (loading) return <LoadingState />
  if (rows.length === 0) return <EmptyState title="No connections yet" description="Connect with people from the directory." />
  return (
    <Stack spacing={1.5}>
      {rows.map((c) => (
        <Card key={c.id} variant="outlined" sx={{ p: 2, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              {c.fullName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {c.company}
            </Typography>
          </Box>
          {c.status === 'accepted' && <Chip size="small" color="success" label="Connected" />}
          {c.status === 'pending' && c.direction === 'incoming' && (
            <>
              <Button size="small" variant="contained" onClick={() => respond(c.id, true)}>
                Accept
              </Button>
              <Button size="small" onClick={() => respond(c.id, false)}>
                Decline
              </Button>
            </>
          )}
          {c.status === 'pending' && c.direction === 'outgoing' && <Chip size="small" label="Requested" />}
          {c.status === 'declined' && <Chip size="small" label="Declined" />}
        </Card>
      ))}
    </Stack>
  )
}

function MeetingsTab() {
  const [rows, setRows] = useState<MeetingRow[]>([])
  const [loading, setLoading] = useState(true)
  const load = () => networkingService.meetings().then(setRows).catch(() => setRows([])).finally(() => setLoading(false))
  useEffect(() => {
    load()
  }, [])
  const respond = async (id: string, accept: boolean) => {
    try {
      await networkingService.respondMeeting(id, accept)
    } catch {
      /* conflict etc. */
    }
    load()
  }
  const cancel = async (id: string) => {
    await networkingService.cancelMeeting(id)
    load()
  }
  if (loading) return <LoadingState />
  if (rows.length === 0) return <EmptyState title="No meetings yet" description="Request a meeting from the directory." />
  return (
    <Stack spacing={1.5}>
      {rows.map((m) => (
        <Card key={m.id} variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                {m.title || `Meeting with ${m.withName}`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {m.withName} · {formatDayLabel(m.startsAt)} · {formatTimeRange(m.startsAt, m.endsAt)}
                {m.location ? ` · ${m.location}` : ''}
              </Typography>
            </Box>
            <Chip
              size="small"
              label={m.status}
              color={m.status === 'accepted' ? 'success' : m.status === 'pending' ? 'warning' : 'default'}
              sx={{ textTransform: 'capitalize', fontWeight: 700 }}
            />
          </Stack>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
            {m.status === 'pending' && m.role === 'invitee' && (
              <>
                <Button size="small" variant="contained" onClick={() => respond(m.id, true)}>
                  Accept
                </Button>
                <Button size="small" onClick={() => respond(m.id, false)}>
                  Decline
                </Button>
              </>
            )}
            {(m.status === 'pending' || m.status === 'accepted') && (
              <AddToCalendarButton meeting={m} />
            )}
            {(m.status === 'pending' || m.status === 'accepted') && (
              <Button size="small" color="error" onClick={() => cancel(m.id)}>
                Cancel
              </Button>
            )}
          </Stack>
        </Card>
      ))}
    </Stack>
  )
}

/** Split "Add to calendar" control: primary = last-used provider, ▾ = the rest. */
function AddToCalendarButton({ meeting }: { meeting: MeetingRow }) {
  const [preferred, setPreferred] = useState<CalProvider>(() => getPreferredProvider())
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)

  const calEvent = (): CalEvent => ({
    title: meeting.title || `Meeting with ${meeting.withName ?? 'attendee'}`,
    startsAt: meeting.startsAt,
    endsAt: meeting.endsAt,
    location: meeting.location ?? undefined,
    description:
      `1:1 meeting with ${meeting.withName ?? 'an attendee'}` +
      (meeting.status === 'pending' ? ' (pending confirmation)' : ''),
  })

  const pick = (provider: CalProvider) => {
    addToCalendar(provider, calEvent())
    setPreferred(provider)
    setPreferredProvider(provider)
    setAnchor(null)
  }

  return (
    <>
      <ButtonGroup size="small" variant="outlined">
        <Button startIcon={<CalendarMonthRoundedIcon />} onClick={() => pick(preferred)}>
          Add to calendar
        </Button>
        <Button
          size="small"
          onClick={(e) => setAnchor(e.currentTarget)}
          aria-label="Choose calendar"
          sx={{ px: 0.5, minWidth: 0 }}
        >
          <ArrowDropDownRoundedIcon fontSize="small" />
        </Button>
      </ButtonGroup>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {CAL_PROVIDERS.map((p) => (
          <MenuItem key={p} selected={p === preferred} onClick={() => pick(p)}>
            {PROVIDER_LABELS[p]}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

function MeetingDialog({ entry, onClose }: { entry: DirectoryEntry; onClose: () => void }) {
  const [startsAt, setStartsAt] = useState('')
  const [endsAt, setEndsAt] = useState('')
  const [title, setTitle] = useState('')
  const [location, setLocation] = useState('')
  const [conflicts, setConflicts] = useState<string[] | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const check = async () => {
    if (!startsAt || !endsAt) return
    const res = await networkingService.checkMeeting(entry.attendeeId, toIso(startsAt), toIso(endsAt))
    setConflicts(res.conflicts)
  }
  const propose = async () => {
    if (!startsAt || !endsAt) {
      setError('Pick a time')
      return
    }
    setSaving(true)
    setError(null)
    try {
      await networkingService.proposeMeeting({
        attendeeId: entry.attendeeId,
        startsAt: toIso(startsAt),
        endsAt: toIso(endsAt),
        title: title || undefined,
        location: location || undefined,
      })
      onClose()
    } catch (e) {
      setError((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Could not request the meeting')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Request a meeting with {entry.fullName}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Title" fullWidth size="small" value={title} onChange={(e) => setTitle(e.target.value)} />
          <TextField label="Start" type="datetime-local" fullWidth size="small" InputLabelProps={{ shrink: true }} value={startsAt} onChange={(e) => setStartsAt(e.target.value)} onBlur={check} />
          <TextField label="End" type="datetime-local" fullWidth size="small" InputLabelProps={{ shrink: true }} value={endsAt} onChange={(e) => setEndsAt(e.target.value)} onBlur={check} />
          <TextField label="Location" fullWidth size="small" value={location} onChange={(e) => setLocation(e.target.value)} />
          {conflicts && conflicts.length > 0 && (
            <Alert severity="warning">This time {conflicts.join('; ')}.</Alert>
          )}
          {conflicts && conflicts.length === 0 && <Alert severity="success">This time is free.</Alert>}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={propose} disabled={saving}>
          {saving ? 'Sending…' : 'Request'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function toIso(local: string): string {
  return new Date(local).toISOString()
}
